import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Link,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTeacher } from '../../context/TeacherContext';
import apiAxiosInstance from '../../utils/axiosInstance';

interface SignUpModalProps {
  open: boolean;
  onClose: () => void;
}

const SignUpModal: React.FC<SignUpModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { setTeacher } = useTeacher();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState({ 
    email: '', 
    password: '', 
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  
  // Policy acceptance state
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);

    // Validate policy acceptance
    if (!acceptedTerms || !acceptedPrivacy) {
      setErrorMessage('You must accept the Terms of Service and Privacy Policy to continue');
      setLoading(false);
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await apiAxiosInstance.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      
      console.log('Signup response:', response);
      console.log('Response data:', response.data);
      console.log('Has token:', !!response.data.token);
      console.log('Has teacher:', !!response.data.teacher);
      
      // Check for token (backend returns { message, token, teacher })
      if (response.data.token && response.data.teacher) {
        const { teacher, token } = response.data;
        console.log('Setting teacher and token');
        
        // Store token first
        localStorage.setItem('token', token);
        
        // Set teacher in context
        setTeacher(teacher);
        
        console.log('Closing modal');
        onClose();
        
        console.log('Navigating to complete-profile');
        // Use setTimeout to ensure modal closes before navigation
        setTimeout(() => {
          navigate('/complete-profile');
        }, 100);
      } else {
        console.error('Missing token or teacher in response:', response.data);
        setErrorMessage(response.data.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setErrorMessage(
        error.response?.data?.message || 
        'Registration failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle Google OAuth
  const handleGoogleAuth = () => {
    onClose(); // Close modal before redirect
    sessionStorage.setItem('redirectPath', '/complete-profile');
    const authUrl = process.env.NODE_ENV === 'production' 
      ? (process.env.REACT_APP_AUTH_URL || 'https://teach-ai-db-backend.vercel.app/auth')
      : (process.env.REACT_APP_AUTH_URL || 'http://localhost:5000/auth');
    window.location.href = `${authUrl}/google`;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: 'relative' }}>
          {/* Close button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Header */}
          <Box sx={{ p: 3, pb: 0 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              textAlign="center"
              color="primary"
              fontWeight="bold"
              mb={1}
            >
              Welcome to TeachAssist
            </Typography>
            <Typography 
              variant="body1" 
              textAlign="center"
              color="text.secondary"
              mb={3}
            >
              Create AI-powered lesson plans in minutes
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ px: 3, pb: 3 }}>
            {/* Google Sign Up */}
            <Button
              variant="outlined"
              onClick={handleGoogleAuth}
              startIcon={
                <img 
                  src="/google-logo.svg" 
                  alt="Google" 
                  style={{ width: 20, height: 20 }}
                />
              }
              sx={{ 
                width: '100%',
                height: '48px',
                mb: 3,
                fontSize: '1rem',
                textTransform: 'none'
              }}
            >
              Sign up with Google
            </Button>

            <Box sx={{ my: 2 }}>
              <Divider>
                <Typography variant="body2" color="text.secondary">
                  Or continue with email
                </Typography>
              </Divider>
            </Box>

            {/* Sign Up Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  margin="normal"
                  required
                  autoComplete="given-name"
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  margin="normal"
                  required
                  autoComplete="family-name"
                />
              </Box>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
                required
                autoComplete="email"
              />
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                margin="normal"
                required
                autoComplete="new-password"
                helperText="Must be at least 8 characters"
              />
              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                margin="normal"
                required
                autoComplete="new-password"
              />

              {errorMessage && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errorMessage}
                </Alert>
              )}

              {/* Policy Acceptance Checkboxes */}
              <Box 
                sx={{ 
                  mt: 3, 
                  p: 2, 
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'rgba(25, 118, 210, 0.12)'
                }}
              >
                <FormGroup sx={{ gap: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': {
                            color: 'primary.main',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                        I have read and agree to the{' '}
                        <Link
                          component="button"
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowTermsDialog(true);
                          }}
                          sx={{ 
                            cursor: 'pointer', 
                            textDecoration: 'underline',
                            fontWeight: 500,
                            color: 'primary.main',
                            '&:hover': {
                              color: 'primary.dark',
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          Terms of Service
                        </Link>
                      </Typography>
                    }
                    sx={{ 
                      alignItems: 'flex-start',
                      m: 0,
                      '& .MuiCheckbox-root': {
                        pt: 0.5,
                      }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={acceptedPrivacy}
                        onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                        sx={{
                          color: 'primary.main',
                          '&.Mui-checked': {
                            color: 'primary.main',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                        I have read and agree to the{' '}
                        <Link
                          component="button"
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowPrivacyDialog(true);
                          }}
                          sx={{ 
                            cursor: 'pointer', 
                            textDecoration: 'underline',
                            fontWeight: 500,
                            color: 'primary.main',
                            '&:hover': {
                              color: 'primary.dark',
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          Privacy Policy
                        </Link>
                      </Typography>
                    }
                    sx={{ 
                      alignItems: 'flex-start',
                      m: 0,
                      '& .MuiCheckbox-root': {
                        pt: 0.5,
                      }
                    }}
                  />
                </FormGroup>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 3,
                  mb: 2,
                  height: '48px',
                  fontSize: '1.1rem',
                  background: 'linear-gradient(45deg, #1976d2 30%, #ff6b35 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1565c0 30%, #e55a2b 90%)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  }
                }}
                disabled={loading || !acceptedTerms || !acceptedPrivacy}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
              </Button>
            </form>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Already have an account?
              </Typography>
              <Button
                variant="text"
                onClick={() => {
                  onClose();
                  // This will be handled by the parent component
                  window.dispatchEvent(new CustomEvent('openSignInModal'));
                }}
                sx={{ 
                  textTransform: 'none',
                  fontSize: '1rem',
                  color: 'primary.main'
                }}
              >
                Sign In
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* Terms of Service Dialog */}
      <Dialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" component="span">
              Terms of Service
            </Typography>
            <IconButton onClick={() => setShowTermsDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Last updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Effective Date: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>1. Acceptance of Terms</Typography>
            <Typography variant="body2" paragraph>
              Welcome to TeachAssist ("we," "our," or "us"). These Terms of Service ("Terms") 
              govern your use of our educational platform service located at https://levelupteaching.ai 
              (the "Service") operated by Level Up Teaching, LLC.
            </Typography>
            <Typography variant="body2" paragraph>
              By accessing or using our Service, you agree to be bound by these Terms. If you 
              disagree with any part of these terms, then you may not access the Service.
            </Typography>

            <Typography variant="h6" gutterBottom>2. Description of Service</Typography>
            <Typography variant="body2" paragraph>
              TeachAssist is an AI-powered educational platform designed to help teachers and 
              educators create lesson plans, manage students, track progress, and deliver 
              personalized instruction. Our Service includes:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div">• AI-generated lesson plans and educational content</Typography>
              <Typography variant="body2" component="div">• Student management and grouping tools</Typography>
              <Typography variant="body2" component="div">• Assessment creation and grading capabilities</Typography>
              <Typography variant="body2" component="div">• Progress tracking and reporting features</Typography>
              <Typography variant="body2" component="div">• Integration with Google Classroom and other educational platforms</Typography>
            </Typography>

            <Typography variant="h6" gutterBottom>3. User Accounts</Typography>
            <Typography variant="body2" paragraph>
              To use certain features of our Service, you must create an account. You agree to:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div">• Provide accurate, current, and complete information</Typography>
              <Typography variant="body2" component="div">• Maintain and update your account information</Typography>
              <Typography variant="body2" component="div">• Keep your account credentials secure and confidential</Typography>
              <Typography variant="body2" component="div">• Accept responsibility for all activities under your account</Typography>
              <Typography variant="body2" component="div">• Notify us immediately of any unauthorized account use</Typography>
            </Typography>

            <Typography variant="h6" gutterBottom>4. Acceptable Use Policy</Typography>
            <Typography variant="body2" paragraph>
              You agree to use our Service only for lawful purposes and in accordance with these Terms. 
              You agree NOT to use the Service:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div">• In any way that violates applicable laws or regulations</Typography>
              <Typography variant="body2" component="div">• To transmit harmful, offensive, or inappropriate content</Typography>
              <Typography variant="body2" component="div">• To impersonate others or provide false information</Typography>
              <Typography variant="body2" component="div">• To interfere with or disrupt the Service or its servers</Typography>
              <Typography variant="body2" component="div">• To attempt unauthorized access to any part of the Service</Typography>
              <Typography variant="body2" component="div">• To use the Service for commercial purposes without permission</Typography>
            </Typography>

            <Typography variant="h6" gutterBottom>5. Student Data and Educational Records</Typography>
            <Typography variant="body2" paragraph>
              We understand the sensitive nature of educational data. When using our Service:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div">• You remain the owner of all student data you input</Typography>
              <Typography variant="body2" component="div">• You must have proper authorization to input student information</Typography>
              <Typography variant="body2" component="div">• We comply with FERPA and other applicable education privacy laws</Typography>
              <Typography variant="body2" component="div">• Student data is used solely to provide educational services</Typography>
              <Typography variant="body2" component="div">• You can request deletion of student data at any time</Typography>
            </Typography>

            <Typography variant="h6" gutterBottom>6. Intellectual Property Rights</Typography>
            <Typography variant="body2" paragraph>
              The Service and its original content, features, and functionality are owned by 
              Level Up Teaching, LLC and are protected by international copyright, trademark, 
              patent, trade secret, and other intellectual property laws.
            </Typography>
            <Typography variant="body2" paragraph>
              You retain ownership of any educational content you create using our Service. 
              However, you grant us a limited license to use this content solely to provide 
              and improve our Service.
            </Typography>

            <Typography variant="h6" gutterBottom>7. Service Availability</Typography>
            <Typography variant="body2" paragraph>
              We strive to provide reliable service but cannot guarantee:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div">• Uninterrupted or error-free operation</Typography>
              <Typography variant="body2" component="div">• Availability at all times</Typography>
              <Typography variant="body2" component="div">• Compatibility with all systems or devices</Typography>
            </Typography>
            <Typography variant="body2" paragraph>
              We reserve the right to modify, suspend, or discontinue the Service with 
              reasonable notice when possible.
            </Typography>

            <Typography variant="h6" gutterBottom>8. Limitation of Liability</Typography>
            <Typography variant="body2" paragraph>
              To the fullest extent permitted by law, Level Up Teaching, LLC shall not be 
              liable for any indirect, incidental, special, consequential, or punitive damages, 
              including but not limited to loss of profits, data, use, goodwill, or other 
              intangible losses resulting from your use of the Service.
            </Typography>

            <Typography variant="h6" gutterBottom>9. Termination</Typography>
            <Typography variant="body2" paragraph>
              We may terminate or suspend your account and access to the Service immediately, 
              without prior notice or liability, for any reason, including breach of these Terms.
            </Typography>
            <Typography variant="body2" paragraph>
              You may terminate your account at any time by contacting us or using the 
              account deletion feature in your settings.
            </Typography>

            <Typography variant="h6" gutterBottom>10. Changes to Terms</Typography>
            <Typography variant="body2" paragraph>
              We reserve the right to modify these Terms at any time. We will notify users 
              of any material changes by posting the new Terms on this page and updating 
              the "Last updated" date.
            </Typography>

            <Typography variant="h6" gutterBottom>11. Governing Law</Typography>
            <Typography variant="body2" paragraph>
              These Terms shall be governed by and construed in accordance with the laws 
              of the United States, without regard to its conflict of law provisions.
            </Typography>

            <Typography variant="h6" gutterBottom>12. Contact Information</Typography>
            <Typography variant="body2" paragraph>
              If you have any questions about these Terms of Service, please contact us:
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> legal@levelupteaching.ai
            </Typography>
            <Typography variant="body2">
              <strong>Website:</strong> https://levelupteaching.ai
            </Typography>
            <Typography variant="body2">
              <strong>Address:</strong> Level Up Teaching, LLC
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTermsDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Privacy Policy Dialog */}
      <Dialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" component="span">
              Privacy Policy
            </Typography>
            <IconButton onClick={() => setShowPrivacyDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Last updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Effective Date: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>1. Introduction</Typography>
            <Typography variant="body2" paragraph>
              Welcome to Level Up Teaching ("we," "our," or "us"). This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you use our educational platform service 
              located at https://levelupteaching.ai (the "Service").
            </Typography>
            <Typography variant="body2" paragraph>
              We are committed to protecting your privacy and ensuring transparency about our data practices. 
              This policy applies to all users of our Service, including teachers, educators, and school 
              administrators.
            </Typography>

            <Box sx={{ 
              p: 2, 
              mt: 2, 
              mb: 2,
              bgcolor: 'rgba(25, 118, 210, 0.04)',
              border: '1px solid',
              borderColor: 'rgba(25, 118, 210, 0.12)',
              borderRadius: 1
            }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Google Classroom Data Usage
              </Typography>
              <Typography variant="body2" paragraph>
                When you connect your Google Classroom account to our Service, we access Google Classroom 
                data solely to enable core educational features. Specifically:
              </Typography>
              <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                <Typography variant="body2" component="div">• We access student rosters to help you organize reading groups and create differentiated lesson plans</Typography>
                <Typography variant="body2" component="div">• We access course information to help you manage your classes within our platform</Typography>
                <Typography variant="body2" component="div">• This data is used exclusively for educational purposes and is never sold or shared with third parties</Typography>
                <Typography variant="body2" component="div">• You can revoke our access at any time through your Google Account settings</Typography>
              </Typography>
            </Box>

            <Typography variant="h6" gutterBottom>2. Information We Collect</Typography>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>2.1 Information You Provide</Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div" paragraph>
                <strong>Account Information:</strong> Name, email address, profile picture, and authentication credentials when you create an account or sign in via Google or LinkedIn
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <strong>Professional Information:</strong> School name, grade levels taught, subjects taught, years of experience, and professional biography
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <strong>Student Information:</strong> Student names, reading levels, group assignments, and academic progress data (no personally identifiable student information is stored without explicit consent)
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <strong>Educational Content:</strong> Lesson plans, assessments, student work samples, and instructional materials you create or upload
              </Typography>
            </Typography>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>2.2 Information from Google Services</Typography>
            <Typography variant="body2" paragraph>
              When you connect your Google account, we may access:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div">• Google Classroom data (courses, rosters, assignments) - only with your explicit permission</Typography>
              <Typography variant="body2" component="div">• Google Drive files - only files you explicitly share or import into our Service</Typography>
              <Typography variant="body2" component="div">• Basic profile information (name, email, profile picture)</Typography>
            </Typography>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>2.3 Automatically Collected Information</Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div" paragraph>
                <strong>Usage Information:</strong> How you interact with our Service, features used, time spent, and navigation patterns
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <strong>Technical Information:</strong> IP address, browser type, device information, and access times for security and performance purposes
              </Typography>
            </Typography>

            <Typography variant="h6" gutterBottom>3. How We Use Your Information</Typography>
            <Typography variant="body2" paragraph>
              We use the information we collect for the following purposes:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div">• Provide and maintain our educational Service</Typography>
              <Typography variant="body2" component="div">• Generate AI-powered lesson plans and educational content</Typography>
              <Typography variant="body2" component="div">• Manage student groups and track academic progress</Typography>
              <Typography variant="body2" component="div">• Authenticate your account and ensure security</Typography>
              <Typography variant="body2" component="div">• Improve our Service and develop new features</Typography>
              <Typography variant="body2" component="div">• Provide customer support and respond to inquiries</Typography>
              <Typography variant="body2" component="div">• Send important updates about our Service</Typography>
              <Typography variant="body2" component="div">• Comply with legal obligations and protect our rights</Typography>
            </Typography>

            <Typography variant="h6" gutterBottom>4. Information Sharing and Disclosure</Typography>
            <Typography variant="body2" paragraph>
              We do not sell, trade, or rent your personal information to third parties. We may share 
              your information only in the following circumstances:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div" paragraph>
                <strong>Service Providers:</strong> Trusted third-party services that help us operate our platform (OpenAI for AI services, MongoDB for data storage, Vercel for hosting)
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights and safety
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <strong>School Districts:</strong> With your school or district administrators when required for educational compliance or when you explicitly authorize such sharing
              </Typography>
            </Typography>

            <Typography variant="h6" gutterBottom>5. Data Storage and Security</Typography>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>5.1 How We Store Your Information</Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div" paragraph>
                <strong>Secure Cloud Storage:</strong> Data is stored on secure cloud servers with MongoDB Atlas and Vercel, with encryption at rest and in transit
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <strong>Data Protection:</strong> We implement industry-standard security measures including encryption, secure authentication, and regular security audits
              </Typography>
            </Typography>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>5.2 Data Retention</Typography>
            <Typography variant="body2" paragraph>
              We retain your information for as long as your account is active or as needed to provide 
              you services. You may request deletion of your account and data at any time through your 
              account settings or by contacting us.
            </Typography>

            <Typography variant="h6" gutterBottom>6. Your Rights and Choices</Typography>
            <Typography variant="body2" paragraph>
              You have the following rights regarding your personal information:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              <Typography variant="body2" component="div" paragraph>
                <strong>Access:</strong> Request access to the personal information we hold about you
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <strong>Correction:</strong> Update or correct your personal information through your account settings
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <strong>Deletion:</strong> Request deletion of your account and personal data
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <strong>Portability:</strong> Request export of your data in a commonly used format
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <strong>Opt-out:</strong> Unsubscribe from non-essential communications
              </Typography>
            </Typography>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>6.1 Revoking Google Access</Typography>
            <Typography variant="body2" paragraph>
              You can revoke our access to your Google account data at any time by visiting your Google Account Permissions page 
              and removing TeachAssist from your authorized applications.
            </Typography>

            <Typography variant="h6" gutterBottom>7. Children's Privacy (COPPA Compliance)</Typography>
            <Typography variant="body2" paragraph>
              Our Service is designed for use by teachers and educators, not directly by children under 13. 
              We do not knowingly collect personal information from children under 13 without verifiable 
              parental consent or direct authorization from the educational institution.
            </Typography>
            <Typography variant="body2" paragraph>
              When student information is collected through teacher use of our Service, it is done under 
              the educational exception provisions of COPPA and with appropriate school authorization.
            </Typography>

            <Typography variant="h6" gutterBottom>8. International Users</Typography>
            <Typography variant="body2" paragraph>
              If you are accessing our Service from outside the United States, please be aware that your 
              information may be transferred to, stored, and processed in the United States where our 
              servers are located. By using our Service, you consent to this transfer.
            </Typography>

            <Typography variant="h6" gutterBottom>9. Changes to This Privacy Policy</Typography>
            <Typography variant="body2" paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last updated" date. Continued 
              use of our Service after changes constitutes acceptance of the updated policy.
            </Typography>

            <Typography variant="h6" gutterBottom>10. Contact Us</Typography>
            <Typography variant="body2" paragraph>
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> privacy@levelupteaching.ai
            </Typography>
            <Typography variant="body2">
              <strong>Website:</strong> https://levelupteaching.ai
            </Typography>
            <Typography variant="body2">
              <strong>Address:</strong> Level Up Teaching, LLC
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPrivacyDialog(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SignUpModal;
